import {
  Divider,
  EditIcon,
  GKButton,
  Grid2,
  IconButton,
  PopConfirm,
  Show,
  Tab,
  Tabs,
  Typography,
} from "components";
import { useFetchQuery, useMutation } from "hooks/useQueries";
import DashboardLayout from "layouts/DashboardLayout";
import { lazy, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { TAB } from "store/slices/appSlice";
import { useAppDispatch, useAppSelector } from "store/store";
import { FAMILY_BY_ID } from "utils/apiRoutes";
import axiosInstance from "utils/axiosInstance";
import { QueryKeys } from "utils/queryKey";
import EditFamilyModal from "./EditFamilyModal";
import FamilyCard from "./FamilyCard";

const ClientTab = lazy(() => import("./ClientTab"));
const HoldingTab = lazy(() => import("./HoldingTab"));
const PortfolioTab = lazy(() => import("./PortfolioTab"));
const AccessTab = lazy(() => import("./AccessTab"));

const FamilyDetails = () => {
  const navigate = useNavigate();

  const dispatch = useAppDispatch();

  const { familyERMPageTabs } = useAppSelector((state) => state?.app?.tab);

  const tabs = ["Clients", "Holdings", "Portfolios", "Access"];

  const { id } = useParams<{ id: string }>();

  const [openFamilyModal, setOpenFamilyModal] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const { data, isFetching } = useFetchQuery({
    key: [QueryKeys.FAMILY_LIST, id],
    route: FAMILY_BY_ID(id),
  });

  const { mutate: handleDeleteFamily, isLoading: deleteLoading } = useMutation({
    mutationFn: (id: any) => axiosInstance.delete(`/family/${id}/`),
    onSuccess: () => {
      navigate(-1);
    },
  });

  return (
    <DashboardLayout title="Family Details" loading={isFetching}>
      <Grid2 container spacing={1}>
        <Grid2 size={12} display={"flex"} alignItems={"center"}>
          <Typography fontWeight={600} fontSize={20}>
            {data?.data?.name}
            <span style={{ marginLeft: "8px", fontSize: 12, fontWeight: 500 }}>
              {data?.data?.code}
            </span>
          </Typography>
          <IconButton
            sx={{
              padding: "6px",
              marginLeft: "8px",
            }}
            onClick={() => setOpenFamilyModal(true)}
          >
            <EditIcon width={18} height={18} />
          </IconButton>
        </Grid2>
        <Grid2 size={12}>
          <FamilyCard />
        </Grid2>
        <Grid2 size={12}>
          <Divider />
          <Grid2
            size={12}
            container
            display={"flex"}
            justifyContent={"space-between"}
            alignItems={"center"}
          >
            <Tabs
              variant="standard"
              value={familyERMPageTabs}
              onChange={(e, val) => {
                dispatch(TAB({ tabKey: "familyERMPageTabs", tabValue: val }));
              }}
            >
              {tabs?.map((dataItem, index) => (
                <Tab key={index} label={dataItem} disableRipple />
              ))}
            </Tabs>
            <Show.When isTrue={familyERMPageTabs === 3}>
              <GKButton
                onClick={(e) => setAnchorEl(e.currentTarget)}
                color="error"
                variant="outlined"
                size="medium"
                loading={deleteLoading}
              >
                Delete Family
              </GKButton>
            </Show.When>
          </Grid2>
          <Divider />
        </Grid2>
        <Grid2 size={12}>
          {familyERMPageTabs === 0 && <ClientTab data={data} />}
          {familyERMPageTabs === 1 && <HoldingTab />}
          {familyERMPageTabs === 2 && <PortfolioTab />}
          {familyERMPageTabs === 3 && <AccessTab />}
        </Grid2>
      </Grid2>

      <Show.When isTrue={openFamilyModal}>
        <EditFamilyModal
          open={openFamilyModal}
          setOpen={setOpenFamilyModal}
          data={data?.data}
        />
      </Show.When>

      <Show.When isTrue={Boolean(anchorEl)}>
        <PopConfirm
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          setOpen={setAnchorEl}
          onSuccess={() => handleDeleteFamily(id)}
          message="Are you sure you want to delete this family? This family will permanently be removed. This action cannot be undone."
        />
      </Show.When>
    </DashboardLayout>
  );
};

export default FamilyDetails;
